import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Hexagon, ArrowRight, ArrowLeft, ChevronRight } from "lucide-react";
import track from "@/lib/track";
import {
  PROBLEMS, WHY_BUILT, PERSONAS_EXTERNAL, WORKFLOW, MODULES, COLLAB_FLOW,
  DIFFERENTIATORS, PHILOSOPHY, DELIVERABLES, ENGAGEMENTS, OUTCOMES, TRUST, VISION,
} from "./presentationData";

const HEAD = { fontFamily: "'Outfit', sans-serif" };
const BODY = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const ease = [0.16, 1, 0.3, 1];

const chunk = (arr, n) => arr.reduce((a, x, i) => { (a[Math.floor(i / n)] ||= []).push(x); return a; }, []);

/* ---------- compact building blocks (sized to fit one screen, no scroll) ---------- */
const Kicker = ({ n, children }) => (
  <div className="flex items-center justify-center gap-3 mb-3">
    <span className="font-mono text-[11px] font-semibold text-blue-700 tabular-nums">{String(n).padStart(2, "0")}</span>
    <span className="h-px w-8 bg-blue-300" />
    <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500" style={BODY}>{children}</span>
  </div>
);

const Title = ({ children }) => (
  <h2 className="text-2xl sm:text-3xl lg:text-[2.6rem] font-bold tracking-tight text-slate-900 leading-tight text-center" style={HEAD}>{children}</h2>
);
const Sub = ({ children }) => (
  <p className="mt-2.5 text-sm lg:text-base text-slate-500 max-w-2xl mx-auto text-center leading-relaxed" style={BODY}>{children}</p>
);

const Tile = ({ icon: Icon, title, text }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 lg:p-5 text-left hover:border-blue-300 transition-colors">
    <div className="flex items-center gap-2.5">
      <span className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-blue-600" strokeWidth={1.5} /></span>
      <h3 className="font-semibold text-slate-900 text-sm lg:text-[15px]" style={HEAD}>{title}</h3>
    </div>
    <p className="mt-2 text-xs lg:text-[13px] text-slate-500 leading-relaxed" style={BODY}>{text}</p>
  </div>
);

const FlowGrid = ({ steps, cols = 5 }) => (
  <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-${cols} gap-2.5 lg:gap-3`}>
    {steps.map((s, i) => (
      <div key={i} className="relative rounded-xl border border-slate-200 bg-white px-3 py-3 flex items-center gap-2.5 text-left">
        <span className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><s.icon className="h-4 w-4 text-blue-600" strokeWidth={1.5} /></span>
        <div className="min-w-0">
          <div className="font-mono text-[9px] text-slate-400">{String(i + 1).padStart(2, "0")}</div>
          <div className="text-xs lg:text-[13px] font-medium text-slate-800 leading-tight" style={HEAD}>{s.label}</div>
        </div>
        {i < steps.length - 1 && <ChevronRight className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-300 z-10" />}
      </div>
    ))}
  </div>
);

const ModuleSlideCard = ({ m, i, scientific }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 text-left flex flex-col">
    <div className="flex items-start gap-3">
      <span className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center shrink-0"><m.icon className="h-4.5 w-4.5 text-white" strokeWidth={1.5} /></span>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5"><span className="font-mono text-[9px] text-blue-600">M{String(i + 1).padStart(2, "0")}</span>
          <h3 className="font-semibold text-slate-900 text-sm" style={HEAD}>{m.name}</h3></div>
        <p className="text-xs text-slate-600 leading-snug mt-0.5" style={BODY}>{m.purpose}</p>
        <p className="text-xs text-blue-700 font-medium mt-1" style={BODY}>→ {m.impact}</p>
      </div>
    </div>
    {scientific && (
      <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-x-4 gap-y-2">
        {[["Why it exists", m.why], ["Input", m.input], ["AI analysis", m.ai], ["Human validation", m.human], ["Output", m.output]].map(([k, v]) => (
          <div key={k}>
            <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400" style={BODY}>{k}</div>
            <div className="text-[11px] text-slate-700 leading-snug" style={BODY}>{v}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* ---------- slide definitions ---------- */
const buildSlides = (mode) => {
  const scientific = mode === "scientific";
  const slides = [];

  slides.push({ id: "title", label: "JCA Matrix", render: () => (
    <div className="text-center max-w-4xl mx-auto">
      <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3.5 py-1.5 text-[11px] font-semibold text-blue-700 uppercase tracking-wider">
        <span className="h-1.5 w-1.5 rounded-full bg-blue-600 animate-pulse" /> JCA Readiness & Strategy Platform
      </span>
      <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.06]" style={HEAD}>
        Turning fragmented evidence into <span className="text-blue-700">board-ready JCA decisions.</span>
      </h1>
      <p className="mt-5 text-base lg:text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed" style={BODY}>
        MarksMan Healthcare's proprietary operating system for the EU Joint Clinical Assessment — AI-assisted, consultant-led, fully traceable.
      </p>
      <p className="mt-8 text-xs text-slate-400" style={BODY}>Use → arrow keys or the controls below to navigate</p>
    </div>
  )});

  slides.push({ id: "problem", label: "The Problem", render: () => (
    <div className="max-w-5xl mx-auto w-full">
      <Kicker n={1}>The Problem</Kicker>
      <Title>EU JCA has fundamentally changed evidence generation.</Title>
      <div className="mt-6 lg:mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{PROBLEMS.map((p, i) => <Tile key={i} {...p} />)}</div>
    </div>
  )});

  slides.push({ id: "why-built", label: "Why We Built It", render: () => (
    <div className="max-w-5xl mx-auto w-full">
      <Kicker n={2}>Why We Built It</Kicker>
      <Title>We kept seeing the same challenges — engagement after engagement.</Title>
      <div className="mt-6 lg:mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{WHY_BUILT.map((p, i) => <Tile key={i} {...p} />)}</div>
    </div>
  )});

  slides.push({ id: "what-is", label: "What Is JCA Matrix", render: () => (
    <div className="max-w-4xl mx-auto w-full">
      <Kicker n={3}>What Is JCA Matrix</Kicker>
      <div className="rounded-2xl bg-slate-900 text-white p-6 sm:p-10">
        <p className="text-lg sm:text-2xl lg:text-[1.7rem] font-semibold leading-snug" style={HEAD}>
          "JCA Matrix is MarksMan Healthcare's proprietary <span className="text-blue-400">JCA Readiness & Strategy Platform</span> that helps consultants transform fragmented evidence into board-ready strategic decisions for EU Joint Clinical Assessments."
        </p>
      </div>
      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        {[["Not sold as software", "There is no login to buy. JCA Matrix is the engine behind the engagement."],
          ["You engage the consultants", "Clients engage MarksMan Healthcare's consulting services — not a subscription."],
          ["It powers every engagement", "JCA Matrix works behind the scenes on every project delivered."]].map(([t, x]) => (
          <div key={t} className="rounded-xl border border-slate-200 p-4 bg-white text-left">
            <h3 className="font-semibold text-slate-900 text-sm" style={HEAD}>{t}</h3>
            <p className="mt-1 text-xs text-slate-500 leading-relaxed" style={BODY}>{x}</p>
          </div>
        ))}
      </div>
    </div>
  )});

  slides.push({ id: "who-uses", label: "Who It Serves", render: () => (
    <div className="max-w-4xl mx-auto w-full">
      <Kicker n={4}>Who It Serves</Kicker>
      <Title>Value for every stakeholder in your organisation.</Title>
      <div className="mt-6 lg:mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {PERSONAS_EXTERNAL.map((p, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-4 bg-white flex gap-3 text-left">
            <p.icon className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" strokeWidth={1.5} />
            <div><div className="font-medium text-slate-900 text-sm" style={HEAD}>{p.role}</div><div className="text-xs text-slate-500 mt-0.5 leading-relaxed" style={BODY}>{p.value}</div></div>
          </div>
        ))}
      </div>
    </div>
  )});

  slides.push({ id: "workflow", label: "End-to-End Workflow", render: () => (
    <div className="max-w-5xl mx-auto w-full">
      <Kicker n={5}>End-to-End Workflow</Kicker>
      <Title>From portfolio to executive decision — one continuous flow.</Title>
      <Sub>Every stage feeds the next. Nothing is lost between silos.</Sub>
      <div className="mt-6 lg:mt-8"><FlowGrid steps={WORKFLOW} cols={5} /></div>
    </div>
  )});

  const perSlide = scientific ? 2 : 5;
  chunk(MODULES, perSlide).forEach((group, gi, arr) => {
    slides.push({ id: `modules-${gi + 1}`, label: `Modules ${gi + 1}/${arr.length}`, render: () => (
      <div className="max-w-5xl mx-auto w-full">
        <Kicker n={6}>The Modules · {gi + 1} of {arr.length}</Kicker>
        <Title>Every capability, explained.</Title>
        {!scientific && gi === 0 && <Sub>Switch to "Scientific Depth" above to reveal inputs, AI analysis, validation and outputs.</Sub>}
        <div className={`mt-6 lg:mt-8 grid gap-3 ${scientific ? "lg:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-2"}`}>
          {group.map((m) => <ModuleSlideCard key={m.name} m={m} i={MODULES.indexOf(m)} scientific={scientific} />)}
        </div>
      </div>
    )});
  });

  slides.push({ id: "collaboration", label: "AI + Human", render: () => (
    <div className="max-w-5xl mx-auto w-full">
      <Kicker n={7}>AI + Human</Kicker>
      <Title>AI does the scale. Consultants own the judgment.</Title>
      <div className="mt-6 lg:mt-8"><FlowGrid steps={COLLAB_FLOW} cols={4} /></div>
      <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 px-5 py-3.5 flex items-center justify-center gap-2.5">
        <span className="h-2 w-2 rounded-full bg-blue-600" />
        <p className="text-sm text-slate-700 font-medium" style={BODY}>No recommendation ships without an experienced consultant's sign-off.</p>
      </div>
    </div>
  )});

  slides.push({ id: "differentiators", label: "Differentiators", render: () => (
    <div className="max-w-5xl mx-auto w-full">
      <Kicker n={8}>Differentiators</Kicker>
      <Title>Eleven capabilities most teams tackle with spreadsheets.</Title>
      <div className="mt-6 lg:mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {DIFFERENTIATORS.map((d, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex gap-2.5 text-left">
            <d.icon className="h-4.5 w-4.5 text-blue-600 shrink-0 mt-0.5" strokeWidth={1.5} />
            <div><div className="font-semibold text-slate-900 text-[13px]" style={HEAD}>{d.name}</div><div className="text-[11px] text-slate-500" style={BODY}>{d.text}</div></div>
          </div>
        ))}
      </div>
    </div>
  )});

  slides.push({ id: "philosophy", label: "Philosophy", render: () => (
    <div className="max-w-5xl mx-auto w-full">
      <Kicker n={9}>Philosophy</Kicker>
      <Title>We don't summarize documents. We build intelligence.</Title>
      <div className="mt-6 lg:mt-8 grid grid-cols-2 lg:grid-cols-5 gap-3">
        {PHILOSOPHY.map((p, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 text-center">
            <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center mx-auto"><p.icon className="h-4.5 w-4.5 text-blue-600" strokeWidth={1.5} /></div>
            <h3 className="mt-2.5 font-semibold text-slate-900 text-xs lg:text-[13px]" style={HEAD}>{p.title}</h3>
            <p className="mt-1 text-[11px] text-slate-500 leading-snug" style={BODY}>{p.text}</p>
          </div>
        ))}
      </div>
    </div>
  )});

  slides.push({ id: "deliverables", label: "Deliverables", render: () => (
    <div className="max-w-5xl mx-auto w-full">
      <Kicker n={10}>Deliverables</Kicker>
      <Title>What clients actually receive.</Title>
      <div className="mt-6 lg:mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {DELIVERABLES.map((d, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 text-left">
            <div className="flex items-center justify-between">
              <span className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center"><d.icon className="h-4.5 w-4.5 text-white" strokeWidth={1.5} /></span>
              <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400" style={BODY}>{d.who}</span>
            </div>
            <h3 className="mt-3 font-semibold text-slate-900 text-sm" style={HEAD}>{d.name}</h3>
            <p className="mt-1 text-xs text-slate-500 leading-relaxed" style={BODY}>{d.text}</p>
          </div>
        ))}
      </div>
    </div>
  )});

  slides.push({ id: "engagements", label: "Engagement Models", render: () => (
    <div className="max-w-4xl mx-auto w-full">
      <Kicker n={11}>Engagement Models</Kicker>
      <Title>Four ways to work with MarksMan.</Title>
      <div className="mt-6 lg:mt-8 grid sm:grid-cols-2 gap-3">
        {ENGAGEMENTS.map((e, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-5 flex gap-3.5 text-left">
            <span className="font-mono text-sm text-blue-600 shrink-0">0{i + 1}</span>
            <div>
              <div className="flex items-center gap-2"><e.icon className="h-4.5 w-4.5 text-blue-600" strokeWidth={1.5} /><h3 className="font-semibold text-slate-900 text-[15px]" style={HEAD}>{e.name}</h3></div>
              <p className="mt-1.5 text-xs text-slate-500 leading-relaxed" style={BODY}>{e.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )});

  slides.push({ id: "outcomes", label: "Why MarksMan", render: () => (
    <div className="max-w-5xl mx-auto w-full">
      <Kicker n={12}>Why MarksMan</Kicker>
      <Title>Clients choose us for outcomes, not features.</Title>
      <div className="mt-6 lg:mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{OUTCOMES.map((p, i) => <Tile key={i} {...p} />)}</div>
    </div>
  )});

  slides.push({ id: "trust", label: "Trust & Validation", render: () => (
    <div className="max-w-5xl mx-auto w-full">
      <Kicker n={13}>Trust & Validation</Kicker>
      <Title>Confidence you can defend.</Title>
      <div className="mt-6 lg:mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{TRUST.map((p, i) => <Tile key={i} {...p} />)}</div>
    </div>
  )});

  slides.push({ id: "vision", label: "The Vision", render: () => (
    <div className="max-w-5xl mx-auto w-full">
      <Kicker n={14}>The Vision</Kicker>
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-blue-900 text-white p-6 sm:p-8">
        <p className="text-base sm:text-xl lg:text-[1.45rem] font-semibold leading-snug" style={HEAD}>
          "JCA Matrix is evolving from a project support platform into a comprehensive JCA Readiness & Strategy Platform for EU Joint Clinical Assessments."
        </p>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {VISION.map((p, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex gap-2.5 text-left">
            <p.icon className="h-4.5 w-4.5 text-blue-600 shrink-0 mt-0.5" strokeWidth={1.5} />
            <div><div className="font-semibold text-slate-900 text-[13px]" style={HEAD}>{p.title}</div><div className="text-[11px] text-slate-500 leading-snug" style={BODY}>{p.text}</div></div>
          </div>
        ))}
      </div>
    </div>
  )});

  slides.push({ id: "cta", label: "Next Step", render: () => (
    <div className="text-center max-w-3xl mx-auto">
      <h2 className="text-3xl sm:text-5xl font-bold tracking-tight text-slate-900" style={HEAD}>See what JCA Matrix could do for your asset.</h2>
      <p className="mt-4 text-slate-500" style={BODY}>Talk to a MarksMan Healthcare consultant about a JCA Evidence Stress Test.</p>
      <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-600 text-white px-7 py-3.5 text-sm font-semibold hover:bg-blue-700 transition-colors" data-testid="footer-cta">
        Talk to MarksMan <ArrowRight className="h-4 w-4" />
      </Link>
      <div className="mt-10 text-xs text-slate-400" style={BODY}>© {new Date().getFullYear()} MarksMan Healthcare · JCA Matrix™ · Confidential</div>
    </div>
  )});

  return slides;
};

const AudienceToggle = ({ mode, setMode }) => (
  <div className="flex items-center rounded-full border border-slate-200 bg-white p-0.5" style={BODY} data-testid="audience-toggle">
    {[["business", "Business"], ["scientific", "Scientific Depth"]].map(([v, l]) => (
      <button key={v} onClick={() => setMode(v)} data-testid={`audience-${v}`}
        className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${mode === v ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-800"}`}>
        {l}
      </button>
    ))}
  </div>
);

export default function Presentation() {
  useEffect(() => { track("pageview", "/presentation"); track("presentation_opened", "/presentation"); }, []);
  const [mode, setMode] = useState("business");
  const [[idx, dir], setIdx] = useState([0, 0]);
  const slides = buildSlides(mode);
  const total = slides.length;
  const touchX = useRef(null);

  useEffect(() => { setIdx(([i]) => [Math.min(i, buildSlides(mode).length - 1), 0]); }, [mode]);

  const go = useCallback((next) => {
    setIdx(([i]) => {
      const n = Math.max(0, Math.min(total - 1, next));
      return [n, n > i ? 1 : -1];
    });
  }, [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); go(idx + 1); }
      if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); go(idx - 1); }
      if (e.key === "Home") go(0);
      if (e.key === "End") go(total - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, go, total]);

  const slide = slides[idx];

  return (
    <div className="h-screen flex flex-col bg-white text-slate-900 overflow-hidden select-none" style={BODY} data-testid="presentation-page">
      {/* header */}
      <header className="shrink-0 z-30 backdrop-blur-xl bg-white/80 border-b border-slate-200/60">
        <div className="px-4 sm:px-8 h-14 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="present-logo">
            <span className="h-7 w-7 rounded-lg bg-blue-600 flex items-center justify-center"><Hexagon className="h-4 w-4 text-white" fill="white" /></span>
            <span className="font-semibold tracking-tight text-sm" style={HEAD}>JCA Matrix<sup className="text-[8px] text-slate-400">™</sup></span>
          </Link>
          <div className="flex items-center gap-2.5">
            <AudienceToggle mode={mode} setMode={setMode} />
            <Link to="/contact" className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-blue-700 transition-colors" data-testid="present-cta">
              Talk to MarksMan <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
        {/* progress bar */}
        <div className="h-0.5 bg-slate-100"><div className="h-full bg-blue-600 transition-all duration-400" style={{ width: `${((idx + 1) / total) * 100}%` }} data-testid="deck-progress" /></div>
      </header>

      {/* slide stage */}
      <div className="relative flex-1 min-h-0"
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (dx < -50) go(idx + 1); else if (dx > 50) go(idx - 1);
          touchX.current = null;
        }}>
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-blue-50/50 via-white to-white" />
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div key={`${slide.id}-${mode}`} custom={dir}
            initial={{ opacity: 0, x: dir >= 0 ? 90 : -90 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir >= 0 ? -70 : 70 }}
            transition={{ duration: 0.45, ease }}
            className="absolute inset-0 flex items-center justify-center px-5 sm:px-10 lg:px-20 py-4 overflow-hidden"
            data-testid={`slide-${slide.id}`}>
            {slide.render()}
          </motion.div>
        </AnimatePresence>

        {/* side arrows (desktop) */}
        {idx > 0 && (
          <button onClick={() => go(idx - 1)} data-testid="deck-prev-side"
            className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-slate-200 bg-white/90 items-center justify-center text-slate-500 hover:text-blue-700 hover:border-blue-300 transition-all shadow-sm">
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        {idx < total - 1 && (
          <button onClick={() => go(idx + 1)} data-testid="deck-next-side"
            className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-slate-200 bg-white/90 items-center justify-center text-slate-500 hover:text-blue-700 hover:border-blue-300 transition-all shadow-sm">
            <ArrowRight className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* bottom controls */}
      <footer className="shrink-0 border-t border-slate-100 bg-white/90 backdrop-blur px-4 sm:px-8 h-14 flex items-center justify-between gap-3">
        <button onClick={() => go(idx - 1)} disabled={idx === 0} data-testid="deck-prev"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-700 disabled:opacity-30 transition-all">
          <ArrowLeft className="h-3.5 w-3.5" /> Prev
        </button>
        <div className="flex items-center gap-1.5 overflow-x-auto max-w-[50vw]" data-testid="deck-dots">
          {slides.map((s, i) => (
            <button key={s.id} onClick={() => go(i)} title={s.label} data-testid={`dot-${s.id}`}
              className={`shrink-0 rounded-full transition-all ${i === idx ? "h-2 w-6 bg-blue-600" : "h-2 w-2 bg-slate-300 hover:bg-slate-400"}`} />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-slate-400 tabular-nums" data-testid="deck-counter">{idx + 1} / {total}</span>
          <button onClick={() => go(idx + 1)} disabled={idx === total - 1} data-testid="deck-next"
            className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 text-white px-4 py-2 text-xs font-semibold hover:bg-blue-700 disabled:opacity-30 transition-all">
            Next <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </footer>
    </div>
  );
}
